/**
 * PATH: packages/bills/src/components/billFieldKeys.ts
 * FILE_ID: BILLS-PKG-CMP-FIELDKEYS-001
 * PURPOSE: One canonical vocabulary of "field keys" shared by the
 *          structured Read view and the edit form, so a click in one
 *          pane can cross-highlight the matching cell in the other.
 *
 * WHY: p1c cross-highlight needs both panes to agree on a stable name
 *      for every field (vendor, totals, and per-line cells). Keeping the
 *      strings in ONE module stops the read view and the form drifting
 *      apart (a missed key = a dead highlight). Pure constants/helpers,
 *      no React — safe to import anywhere.
 */

/** Scalar (header / totals) field keys — match BillEditablePatch names. */
export type BillScalarFieldKey =
  | 'vendor_name'
  | 'vendor_email'
  | 'vendor_abn'
  | 'invoice_number'
  | 'reference'
  | 'invoice_date'
  | 'due_date'
  | 'subtotal'
  | 'gst'
  | 'total'
  | 'currency';

/** Editable columns of a line item. */
export type BillLineColumn = 'description' | 'quantity' | 'unit_amount' | 'line_total';

/** Any field key (scalar or `line.<idx>.<column>`). */
export type BillFieldKey = string;

/** Build the stable key for a line-item cell. */
export function lineFieldKey(idx: number, column: BillLineColumn): BillFieldKey {
  return `line.${idx}.${column}`;
}

/** Parse a line-item key back to {idx, column}; null for scalar keys. */
export function parseLineFieldKey(
  key: BillFieldKey,
): { idx: number; column: BillLineColumn } | null {
  const m = /^line\.(\d+)\.(description|quantity|unit_amount|line_total)$/.exec(key);
  if (!m) return null;
  return { idx: Number(m[1]), column: m[2] as BillLineColumn };
}

/**
 * f(bill-ish record, field key) → the field's CURRENT value as a string
 * (`''` when absent). Used to prefill the PDF's in-place field editor.
 * Accepts any BillDetail-shaped record (including a merged draft preview).
 */
export function billFieldValue(source: unknown, key: BillFieldKey): string {
  if (!source || typeof source !== 'object') return '';
  const rec = source as Record<string, unknown>;
  const line = parseLineFieldKey(key);
  let v: unknown;
  if (line) {
    const items = rec.line_items;
    const item = Array.isArray(items)
      ? (items[line.idx] as Record<string, unknown> | undefined)
      : undefined;
    v = item?.[line.column];
  } else {
    v = rec[key];
  }
  return v === null || v === undefined ? '' : String(v);
}
